import { useMemo, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LockClosedIcon, PlusIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import {
  Alert,
  Badge,
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  PageHeader,
  RiskBadge,
  SkeletonRows,
  type Column,
} from '@csa/ui';
import { PERMISSIONS, type PermissionKey } from '@csa/shared';
import { api } from '../../lib/api.js';
import { SodFindings } from './SodFindings.js';
import { RoleEditor } from './RoleEditor.js';
import type { PermissionCatalogue, RoleView } from './types.js';

/**
 * Permissions that are inert until scoped to a target. Read from the shared
 * catalogue rather than restated, so adding a privileged permission cannot
 * leave this check silently behind.
 */
const SCOPED_PERMISSIONS = new Set<PermissionKey>(
  (Object.keys(PERMISSIONS) as PermissionKey[]).filter(
    (key) => PERMISSIONS[key].requiresTargetScope,
  ),
);

/**
 * Role administration.
 *
 * The list leads with the member count and the SoD state, because those are
 * the two questions asked of a role during an access review: who has it, and
 * does holding it create a conflict.
 */
export function RolesPage(): ReactElement {
  const [editing, setEditing] = useState<RoleView | 'new' | null>(null);

  const roles = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: () => api.get<{ roles: RoleView[] }>('/api/admin/roles'),
  });

  const catalogue = useQuery({
    queryKey: ['admin', 'permissions'],
    queryFn: () => api.get<PermissionCatalogue>('/api/admin/permissions'),
    // The catalogue only changes with a deployment, so it is worth holding on
    // to across navigations rather than refetching for every editor open.
    staleTime: 60 * 60_000,
  });

  const conflicted = useMemo(
    () => (roles.data?.roles ?? []).filter((r) => !r.sod.clean),
    [roles.data],
  );

  const inert = useMemo(
    () =>
      (roles.data?.roles ?? []).filter(
        (r) => r.targetGrants.length === 0 && r.permissions.some((p) => SCOPED_PERMISSIONS.has(p)),
      ),
    [roles.data],
  );

  const columns = useMemo<Column<RoleView>[]>(
    () => [
      {
        key: 'name',
        header: 'Role',
        render: (role) => (
          <div className="flex min-w-0 flex-col">
            <span className="flex items-center gap-2 font-medium text-body">
              {role.name}
              {role.isBuiltin ? (
                <Badge tone="neutral" className="gap-1">
                  <LockClosedIcon className="size-3" aria-hidden="true" />
                  Built-in
                </Badge>
              ) : null}
            </span>
            {role.description ? (
              <span className="truncate text-xs text-muted">{role.description}</span>
            ) : null}
          </div>
        ),
      },
      {
        key: 'permissions',
        header: 'Permissions',
        render: (role) => <span className="csa-numeric">{role.permissions.length}</span>,
      },
      {
        key: 'targets',
        header: 'Scoped targets',
        render: (role) => {
          const count = new Set(role.targetGrants.map((g) => g.targetId)).size;
          // A role can look fully configured and grant nothing: privileged
          // permissions are inert until scoped to a target. Saying so here
          // stops it being mistaken for working access during a review.
          if (count === 0 && role.permissions.some((p) => SCOPED_PERMISSIONS.has(p))) {
            return <Badge tone="warning">Not scoped</Badge>;
          }
          return <span className="csa-numeric">{count || '—'}</span>;
        },
      },
      {
        key: 'members',
        header: 'Members',
        render: (role) => <span className="csa-numeric">{role.memberCount}</span>,
      },
      {
        key: 'sod',
        header: 'Duties',
        render: (role) =>
          role.sod.blocking.length > 0 ? (
            <RiskBadge risk="critical" />
          ) : role.sod.warnings.length > 0 ? (
            <RiskBadge risk="high" />
          ) : (
            <span className="text-xs text-subtle">Clean</span>
          ),
      },
      {
        key: 'actions',
        header: '',
        render: (role) => (
          <Button
            variant="ghost"
            size="xs"
            onClick={() => setEditing(role)}
            aria-label={`${role.isBuiltin ? 'View' : 'Edit'} ${role.name}`}
          >
            {role.isBuiltin ? 'View' : 'Edit'}
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Roles"
        description="A role is a named set of permissions. Privileged permissions only take effect on the targets a role is explicitly scoped to."
        actions={
          <Button
            variant="primary"
            leadingIcon={<PlusIcon className="size-4" aria-hidden="true" />}
            onClick={() => setEditing('new')}
            disabled={!catalogue.data}
          >
            New role
          </Button>
        }
      />

      {conflicted.length > 0 ? (
        <SodFindings
          analysis={{
            blocking: conflicted.flatMap((r) => r.sod.blocking),
            warnings: conflicted.flatMap((r) => r.sod.warnings),
            clean: false,
          }}
          context="role"
        />
      ) : null}

      {inert.length > 0 ? (
        <Alert tone="info" title="Some roles grant nothing yet">
          {inert.map((r) => r.name).join(', ')}{' '}
          {inert.length === 1 ? 'holds a permission that' : 'hold permissions that'} only take
          effect on specific connection targets, and no targets are selected. Register a target and
          scope the role to it.
        </Alert>
      ) : null}

      {roles.isPending ? (
        <SkeletonRows rows={5} />
      ) : roles.error ? (
        <ErrorState
          title="Could not load roles"
          message={roles.error.message}
          action={<Button onClick={() => void roles.refetch()}>Retry</Button>}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={roles.data.roles}
          rowKey={(role) => role.id}
          caption="Configured roles and their permission counts"
          empty={
            <EmptyState
              icon={ShieldCheckIcon}
              title="No roles yet"
              description="Create a role to start granting access."
            />
          }
        />
      )}

      {editing && catalogue.data ? (
        <RoleEditor
          role={editing === 'new' ? null : editing}
          catalogue={catalogue.data}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}
